import React, { useEffect, useRef } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import CircularProgress from "@mui/joy/CircularProgress";
import { useAiStore } from "../../store/aiStore";
import { useListStore } from "../../store/listStore";
import { usePlaceStore } from "../../store/placeStore";
import { useAuthStore } from "../../store/authStore";
import { PlanConfirmation } from "./PlanConfirmation";

export function CommandBar() {
  const {
    isCommandBarOpen,
    state,
    plan,
    results,
    error,
    closeCommandBar,
    generatePlan,
    executePlan,
    reset,
  } = useAiStore();

  const inputRef = useRef<HTMLInputElement>(null);
  const [command, setCommand] = React.useState("");

  useEffect(() => {
    if (isCommandBarOpen && state === "idle") {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isCommandBarOpen, state]);

  const handleSubmit = () => {
    if (!command.trim()) return;
    generatePlan(command.trim());
  };

  const handleApprove = async () => {
    if (!plan) return;
    await executePlan(plan.actions);

    // Refresh stores after execution
    const userId = useAuthStore.getState().user?.id;
    if (userId) {
      useListStore.getState().fetchLists(userId);
      const currentList = useListStore.getState().currentList;
      if (currentList) {
        usePlaceStore.getState().fetchListPlaces(currentList.id);
      }
    }
  };

  const handleCancel = () => {
    reset();
    setCommand("");
  };

  const handleClose = () => {
    closeCommandBar();
    setCommand("");
  };

  const isLoading = state === "loading_plan" || state === "executing";

  return (
    <Modal
      open={isCommandBarOpen}
      onClose={isLoading ? undefined : handleClose}
      data-testid="command-bar-modal"
    >
      <ModalDialog
        sx={{ width: 560, maxWidth: "calc(100vw - 32px)" }}
        variant="outlined"
      >
        <Stack spacing={2}>
          <Typography level="title-md">AI Command</Typography>

          {(state === "idle" || state === "loading_plan") && (
            <Input
              slotProps={{ input: { ref: inputRef } }}
              placeholder='What would you like to do? e.g. "Create a Tokyo list and add Shibuya Crossing to it"'
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              disabled={state === "loading_plan"}
              endDecorator={state === "loading_plan" ? <CircularProgress size="sm" /> : null}
              data-testid="command-bar-input"
            />
          )}

          {state === "loading_plan" && (
            <Typography level="body-sm" color="neutral">
              Planning…
            </Typography>
          )}

          {(state === "showing_plan" || state === "executing") && plan && (
            <PlanConfirmation
              plan={plan}
              isExecuting={state === "executing"}
              onApprove={handleApprove}
              onCancel={handleCancel}
            />
          )}

          {state === "done" && plan && (
            <>
              <PlanConfirmation
                plan={plan}
                results={results}
                isExecuting={false}
                onApprove={handleApprove}
                onCancel={handleCancel}
              />
              <Button
                onClick={handleClose}
                variant="solid"
                color="primary"
                data-testid="command-bar-close"
              >
                Done
              </Button>
            </>
          )}

          {state === "error" && (
            <Stack spacing={1}>
              <Typography level="body-sm" color="danger">
                {error}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="plain"
                  color="neutral"
                  onClick={handleCancel}
                >
                  Try Again
                </Button>
                <Button variant="solid" color="neutral" onClick={handleClose}>
                  Close
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
